import { useState } from "react";
import { useNavigate } from "react-router-dom";
import AppShell from "@/components/layout/AppShell";
import { useAuth } from "@/contexts/AuthContext";
import { useFeatureAccess } from "@/hooks/useFeatureAccess";
import { toast } from "sonner";
import { QrCode, ClipboardList, Users, Shield, BookOpen, Megaphone, MessageSquare, KeyRound, FileText, Smartphone, Bell, BarChart3, Settings, CreditCard, Upload, Utensils, ClipboardCheck, FolderOpen, Activity, Archive, ToggleRight, TrendingUp, Inbox, RefreshCw, CalendarClock, ShoppingCart, Link as LinkIcon } from "lucide-react";

export const FEATURES = [
  {
    icon: QrCode,
    color: "text-teal-600",
    bg: "bg-teal-50",
    title: "QR Keychain Sign-In & Kiosk",
    sidebar: "Sign-In Kiosk -> Kiosk Scanner / Print Keychain Cards",
    marketing: `QR Keychain Sign-In & Kiosk

Parents sign children in and out by holding a laminated keychain card to an iPad camera  -  no app, no typing, no searching for names. The system signs the child in, records the exact time, and updates the daily attendance sheet automatically in real time across every device in your center. One scan. Done.

Key Benefits:
- Hands-free check-in takes under 2 seconds
- Automatically updates the daily sign-in sheet on all devices
- Eliminates lines and paper sign-in sheets at drop-off
- Every pickup is timestamped and documented for compliance
- Keychain cards are inexpensive to print and laminate  -  built to last`,
    howto: [
      "Go to Sign-In Kiosk -> Print Keychain Cards in the sidebar",
      "Select the children you want cards for and choose a card color (Mint, Sky Blue, Lavender, or Peach)",
      "Click Print  -  cut the cards, laminate them, and punch a hole in the top corner",
      "Hand one card to each family at pickup",
      "Open Sign-In Kiosk on your iPad every morning and leave it open all day",
      "Parents hold their keychain card up to the iPad camera  -  child is signed in or out in under 2 seconds and appears on the daily sheet immediately"
    ]
  },
  {
    icon: ClipboardList,
    color: "text-blue-600",
    bg: "bg-blue-50",
    title: "Daily Sign-In Sheet",
    sidebar: "Home -> Daily List tab",
    marketing: `Daily Sign-In Sheet

Your complete daily attendance record in one place. See who is in, who is out, and what time  -  updated in real time across every device in your center. The sheet auto-populates every morning with all enrolled children so nothing is missed. Export or print at the end of any day for your records.

Key Benefits:
- Auto-generates every morning with all enrolled children
- Updates instantly when parents scan QR cards or staff enter times manually
- Syncs across iPad, desktop, and mobile in real time  -  no refresh needed
- Filter by classroom category to view one group at a time
- Full printable and exportable daily record for compliance`,
    howto: [
      "From the Home page, click the Daily List tab",
      "Today's sign-in sheet loads automatically with all enrolled children grouped by classroom",
      "Click any child's row to enter or edit drop-off time, pickup time, and pickup person",
      "QR Kiosk scans update the sheet automatically  -  no manual entry needed",
      "Use the category filters at the top to view one classroom group at a time",
      "Click Print or Export at the top right to save or print the completed daily record"
    ]
  },
  {
    icon: Users,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    title: "Enrolled Children",
    sidebar: "Children -> Enrolled Children",
    marketing: `Enrolled Children

Every child's complete enrollment profile in one organized place  -  contact information, emergency contacts, authorized pickups, classroom assignment, and all compliance documents. Add children manually or upload dozens at once with the bulk upload tool. Child records connect directly to compliance tracking, form submissions, and the parent portal.

Key Benefits:
- Complete child profiles with all parent and emergency contact information
- Bulk upload tool to add many children at once from a spreadsheet
- Direct link to each child's compliance documents and enrollment forms
- Authorized pickup list management for every child
- Seamlessly connected to daily sign-in, QR sign-in, and parent portal`,
    howto: [
      "Go to Children -> Enrolled Children in the sidebar",
      "Click Add Child to create a new enrollment profile manually",
      "Fill in child information, parent contacts, emergency contacts, and classroom assignment",
      "To add many children at once, click Bulk Upload and download the template spreadsheet",
      "Fill in the spreadsheet and re-upload  -  all children are created automatically",
      "Click any child's name to view their full profile, documents, and compliance status"
    ]
  },
  {
    icon: CalendarClock,
    color: "text-teal-600",
    bg: "bg-teal-50",
    title: "Center Tour Scheduling & Live Ratio Compliance",
    sidebar: "Tour & Ratio Planner",
    marketing: `Center Tour Scheduling & Live Ratio Compliance

Schedule and manage prospective family tours on a Month or Week calendar, with automatic email/SMS reminders for both parents and staff. See live child-to-staff ratios for every room in real time  -  who's present, who's covering, and how many children your current staff can support at full configured capacity  -  plus a 30-day Age Transitions review so no child ages out of a room unnoticed. Move a child's ratio count to a different room with Room for Ratios without changing their enrollment category, and share one staff member across two rooms at the same time with Same Time coverage  -  both rooms drop to whichever ratio is stricter and share one combined allowance, with no child ever double-counted. Look ahead with Projected Ratios for any future date and time, based on confirmed room moves and planned withdrawals only.

Key Benefits:
- Month/Week tour calendar with create, edit, and cancel, plus automated parent & staff reminders
- Live room ratios update in real time from today's sign-ins and staff room coverage
- Shows how many children your current staff can support at each room's full configured capacity
- Room for Ratios lets you move a child's ratio count to another room without changing their category
- Same Time staff coverage drops both rooms to the stricter ratio and shares one combined allowance, with no double-counting
- Age Transitions list flags children approaching or past their current room's age limit
- Projected Ratios lets you check staffing needs for any future date and time before it happens
- Planned withdrawals appear right on the calendar on the child's last day`,
    howto: [
      "Go to Tour & Ratio Planner in the sidebar (directly below Children)",
      "Click Schedule Tour to add a prospective family visit, or click an existing tour to edit or cancel it",
      "Use Planner Settings to set up your rooms (age range, capacity, licensed ratio)",
      "Open Manage Assignments to place children in rooms, mark staff as ratio-qualified, and schedule staff room coverage",
      "In Edit Child, use Room for Ratios to move a child's ratio count to a different room without changing their enrollment category",
      "In Staff Records, set a staff member's secondary assignment Coverage Type to Same Time to share them across two rooms at once (Different Shift keeps room math independent)",
      "Check the Age Transitions sidebar for any child nearing or past their room's age limit, and reassign them there",
      "Use Projected Ratios at the bottom to check staffing for any future date and time before it arrives"
    ]
  },
  {
    icon: Upload,
    color: "text-teal-600",
    bg: "bg-teal-50",
    title: "Bulk Upload  -  Children",
    sidebar: "Children -> Enrolled Children -> Bulk Upload",
    marketing: `Bulk Upload  -  Children

Add an entire enrollment class, a new school year cohort, or any group of children in one step. Download the bulk upload template, fill in each child's information in your spreadsheet, and upload  -  every child profile is created instantly with all contact information, classroom assignments, and enrollment details ready to go. No repetitive manual entry.

Key Benefits:
- Upload dozens or hundreds of children at once from a single spreadsheet
- Pre-formatted template ensures every required field is included
- All child profiles created instantly with contact info and classroom assignment
- Existing children are updated  -  not duplicated  -  when re-uploaded
- Compatible with exports from most childcare management systems`,
    howto: [
      "Go to Children -> Enrolled Children in the sidebar",
      "Click the Bulk Upload button at the top of the children list",
      "Click Download Template to get the pre-formatted spreadsheet",
      "Fill in each child's information  -  name, date of birth, parent contacts, classroom, and category",
      "Save the spreadsheet and click Upload to import all children at once",
      "The system creates new profiles and updates existing ones automatically  -  no duplicates"
    ]
  },
  {
    icon: Upload,
    color: "text-violet-600",
    bg: "bg-violet-50",
    title: "Bulk Upload  -  Staff",
    sidebar: "Staff -> Records & Compliance -> Bulk Date Upload",
    marketing: `Bulk Upload  -  Staff Compliance Dates

Update clearance and certification dates for your entire staff in one step. Download the bulk date template, fill in the document dates for every staff member and every clearance type, and upload  -  all expiration dates are calculated and updated automatically. No clicking into individual records. No manual date entry one by one.

Key Benefits:
- Update clearance dates for all staff in a single spreadsheet upload
- Supports all clearance types  -  Child Abuse, Criminal Record, FBI, CPR, Physical, TB, and more
- Expiration dates calculated automatically from the dates you enter
- Digital submission dates are protected  -  bulk upload never overwrites a newer digital date
- Conflict alerts identify any dates that don't match existing digital submissions
- Download a pre-filled template showing your current staff and all clearance types`,
    howto: [
      "Go to Staff -> Records & Compliance in the sidebar",
      "Click Bulk Date Upload at the top of the staff records section",
      "Click Download Template  -  it downloads pre-filled with your current staff names and clearance columns",
      "Fill in the document date for each staff member and clearance type",
      "Upload the completed spreadsheet  -  all dates and expirations update automatically",
      "Any conflicts with digital submissions are flagged in a report  -  review before they take effect"
    ]
  },
  {
    icon: Shield,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    title: "Staff Records & Clearances",
    sidebar: "Staff -> Records & Compliance",
    marketing: `Staff Records & Clearances

Track every staff certification, clearance, and required training in one organized system  -  Child Abuse Clearance, Criminal Record Check, FBI Clearance, CPR, Health & Safety Basics, Physical Exam, TB Test, and more. Set expiration dates, receive automatic alerts before anything expires, and upload the actual documents to each staff profile. Everything you need for your annual state inspection, always ready.

Key Benefits:
- All state-required clearances tracked with automatic expiration calculation
- Document uploads for every clearance  -  no more paper files
- Health & Safety Basics recognized as one-time completion  -  never flags as expired
- Automatic email alerts 30 days before any clearance expires
- Compliance score dashboard shows your overall readiness at a glance`,
    howto: [
      "Go to Staff -> Records & Compliance in the sidebar",
      "Search for or select a staff member",
      "The clearance grid shows all required documents with current status (Current / Expiring / Expired)",
      "Click a row to enter the date on the document  -  expiration is calculated automatically",
      "Click the Upload button on any row to attach a PDF or photo of the actual document",
      "Multiple documents stack with a count badge  -  click to expand and see all uploads",
      "The compliance dashboard at the top shows your center's overall clearance health"
    ]
  },
  {
    icon: BookOpen,
    color: "text-amber-600",
    bg: "bg-amber-50",
    title: "Lesson Planning Hub",
    sidebar: "Lesson Planning Hub in the sidebar",
    marketing: `Lesson Planning Hub

Build and print weekly lesson plans for every classroom in minutes. Choose from age-specific templates designed for Infant, Toddler, Pre-K, Pre-K2, and School Age groups. Fill in your activities for each time slot and day of the week, add your weekly theme, and print a professionally formatted plan ready to post on your board or file for inspection. Your center name and logo are included automatically.

Key Benefits:
- Age-specific templates for every classroom type
- All five days and all daily time slots organized in one grid
- Professional printable layout  -  all content fits on one landscape page
- Your center name and logo appear automatically on every print
- Save drafts and come back to finish  -  no work is ever lost`,
    howto: [
      "Go to Lesson Planning Hub in the sidebar",
      "Click your classroom type (Infant, Toddler, Pre-K, etc.)",
      "Select the week using the date picker",
      "Click Create New Plan or open an existing one",
      "Fill in your activities for each time slot across all five days",
      "Add your weekly theme at the top",
      "Click Print  -  a formatted one-page plan opens ready to print or save as PDF"
    ]
  },
  {
    icon: Megaphone,
    color: "text-teal-600",
    bg: "bg-teal-50",
    title: "Family Communications Hub",
    sidebar: "Family Communications Hub in the sidebar",
    marketing: `Family Communications Hub

Reach every enrolled family with one message. Send daily menus, class project updates, items needed from home, and center announcements by email and text simultaneously. Messages appear instantly in the parent's Family Portal app, keeping every family connected and informed  -  without phone tag, group texts, or paper notes.

Key Benefits:
- One message reaches all families by email AND text at the same time
- Four communication types: Daily Menu, Class Project, Items Needed, Announcement
- Send to all parents, a specific classroom, or individual families
- Attach photos, files, and documents to any message
- Message history saved and viewable by the director at any time
- Requires Family SMS Access Portal subscription`,
    howto: [
      "Go to Family Communications Hub in the sidebar",
      "Click Send to Parents (the default tab)",
      "Choose your communication type: Menu, Class Project, Items Needed, or Announcement",
      "Select your recipients  -  All Parents, a specific classroom, or individual families",
      "Type your message and attach a photo or file if needed",
      "Toggle Email and/or Text (SMS) for delivery method",
      "Click Send  -  parents receive the message in their portal and by the delivery method you chose"
    ]
  },
  {
    icon: Bell,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    title: "Center Message Board & SMS Alerts",
    sidebar: "Dashboard -> Center Message (bell/notepad icon)",
    marketing: `Center Message Board & SMS Alerts

Post a daily message to your entire team that appears on the main dashboard with a notification beep  -  so every staff member sees it the moment they open the app. Never miss a shift change note, policy reminder, or urgent update again.

New  -  SMS Notification for Owners:
When you are away from the app, get an automatic text the moment any center message is posted. Set up one or more notification phone numbers in your Profile and every new message triggers an instant SMS alert so you are always in the loop.

Key Benefits:
- Message posts to all staff instantly on the main dashboard
- Audible beep alerts staff that a new message is waiting
- Owner and designated staff receive an SMS the moment a message is posted
- No app required to receive the notification  -  just a text
- Configure up to 5 notification numbers in Profile Settings
- SMS requires Premium plan + Parent Communication subscription`,
    howto: [
      "Post a message: Go to Dashboard and click the message/notepad area at the top",
      "Type your center message and click Save  -  all staff see it immediately with a beep",
      "Set up SMS alerts: Go to Profile (top-right avatar) -> scroll to Center Message SMS Notifications",
      "Click + Add number and enter the cell phone number(s) that should receive alerts",
      "Click Save Notification Numbers  -  setup is complete",
      "From now on, every time a center message is posted, those numbers receive: New center message from [Name]: [preview]",
      "To remove a number, return to Profile and click the X next to it"
    ]
  },
  {
    icon: MessageSquare,
    color: "text-purple-600",
    bg: "bg-purple-50",
    title: "Parent SMS Communications",
    sidebar: "Revenue Hub -> Parent SMS Communications",
    marketing: `Parent SMS Communications

Manage all parent text message consent and send direct SMS alerts from one professional platform. Every parent's opt-in is tracked and stored automatically  -  fully compliant with A2P 10DLC carrier requirements. Send urgent pickup alerts, center closures, and important reminders directly to parent phones with complete delivery confirmation.

Key Benefits:
- Full A2P 10DLC carrier compliance  -  messages actually reach parents
- Every parent's consent status tracked automatically (Opted In / Opted Out / Pending)
- Send to all opted-in parents or a specific group
- Delivery confirmation for every message sent
- Parents can opt out at any time  -  system updates automatically`,
    howto: [
      "Go to Revenue Hub -> Parent SMS Communications in the sidebar",
      "Subscribe to activate (monthly or annual plan)",
      "View the consent dashboard showing opt-in status for every parent",
      "Click Compose Message to write a text to all opted-in parents or a specific group",
      "Review the delivery report after sending to confirm receipt",
      "Parents who text STOP are automatically removed from future sends"
    ]
  },
  {
    icon: KeyRound,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    title: "Family SMS Access Portal",
    sidebar: "Family Communications Hub -> Family SMS Access Portal",
    marketing: `Family SMS Access Portal

Give parents their own secure digital portal  -  and create a new revenue stream for your center. Activate any enrolled child with a single click, send a personalized invitation by email or text, and parents are set up with a free trial in minutes. Set your own monthly fee per child and keep 100% of the revenue. The portal handles billing automatically so you never have to chase a payment.

Key Benefits:
- Your own monthly fee per child  -  you set the price, you keep the revenue
- One-click parent activation with automatic email or text invitation
- Parents get a free trial period so they can experience the portal before paying
- Automatic recurring billing  -  Square handles payment collection for you
- Real-time portal access management  -  activate, revoke, or re-activate any parent instantly
- Zelle payment tracking and overdue reminders built in`,
    howto: [
      "Go to Family Communications Hub -> Family SMS Access Portal in the sidebar",
      "Subscribe your center using the monthly or annual plan",
      "Set your monthly fee per child in the Parent Billing Setup section",
      "Check the box next to each child you want to activate",
      "Toggle Email and/or Text, then click Send Invite",
      "The parent receives their invitation, creates a password, and begins their free trial",
      "Active parents appear in your billing list  -  the portal collects monthly fees automatically"
    ]
  },
  {
    icon: FileText,
    color: "text-teal-600",
    bg: "bg-teal-50",
    title: "Enrollment Forms Builder",
    sidebar: "Family Communications Hub -> Enrollment Forms",
    marketing: `Enrollment Forms Builder

Build, customize, and publish digital enrollment forms that parents complete and sign directly from their phone or computer  -  no printing, no scanning, no chasing paper. Every form includes your center name, your state's regulatory codes, and a legally binding e-signature compliant with the ESIGN Act. Set annual or custom expiration periods so forms automatically prompt parents to update and re-sign when due.

Key Benefits:
- 6 pre-built templates covering all standard enrollment documents  -  fully editable
- Legally binding digital e-signature compliant with federal ESIGN Act
- Set expiration periods  -  forms automatically notify parents to re-sign when due
- Custom date auto-fill for bulk annual sends  -  set once, applies to all parents
- State regulatory codes, citations, and required text in dedicated header and footer
- Submissions stored securely and connected to each child's profile
- Print-ready PDF output with your logo and center name on every page`,
    howto: [
      "Go to Family Communications Hub -> Enrollment Forms in the sidebar",
      "Click New Form  -  choose Start from Scratch or pick one of 6 pre-built templates",
      "Edit field labels, add or remove fields, and reorder using the drag handle",
      "In the Settings panel (left column), add your state's regulatory codes to the Header or Footer",
      "Set an expiration period under Expiration Settings  -  choose from monthly, 6 months, yearly, or custom",
      "Click Publish  -  the form instantly appears in every enrolled parent's portal",
      "When a parent submits, you receive an email notification and the submission appears in your inbox",
      "View all submissions in the Submissions tab  -  filter by form, mark as reviewed, or download"
    ]
  },
  {
    icon: Smartphone,
    color: "text-blue-600",
    bg: "bg-blue-50",
    title: "Parent Portal (Parent App)",
    sidebar: "Accessible by parents at your center's login URL",
    marketing: `Parent Portal

Parents get their own secure mobile portal  -  no separate app download required. Everything works in any mobile browser. Parents can sign children in and out with their QR keychain card, complete and sign enrollment forms, view daily class updates and announcements, pay their monthly portal fee, and send messages directly to the center. The portal is branded with your center name and feels like your own app.

Key Benefits:
- No app store download  -  works in any mobile browser instantly
- QR sign-in directly from the parent's phone  -  just open the portal and scan
- Complete and sign all enrollment forms digitally from their phone
- View daily menus, class projects, announcements, and items-needed messages
- Pay monthly portal fees securely through Square
- Direct messaging to the center  -  responses go to your staff inbox`,
    howto: [
      "Parents access their portal at your center's login URL (shared in their invitation email or text)",
      "They create a password using the invitation link  -  no app store download needed",
      "From the home screen, parents can tap to sign in/out, view class updates, complete forms, and pay",
      "The Enrollment Forms section shows all published forms with status (Not Started / Submitted / Expiring)",
      "Expired or expiring forms show a red or amber badge  -  parent taps to update and re-sign with previous answers pre-filled",
      "Messages sent by your center appear in the parent's updates feed immediately"
    ]
  },
  {
    icon: TrendingUp,
    color: "text-emerald-700",
    bg: "bg-emerald-50",
    title: "Revenue Hub",
    sidebar: "$$$ Revenue Hub in the sidebar",
    marketing: `Revenue Hub  -  Turn Your Center Into a Revenue-Generating Platform

The Revenue Hub is where your childcare management app becomes a business tool. It groups every feature that can generate additional income for your center  -  SMS alert subscriptions, parent communication services, and the Family Access Portal  -  all in one dedicated section. Each feature is an optional paid add-on that you activate when you're ready and charge parents for directly.

Key Benefits:
- Groups all revenue-generating features in one easy-to-find sidebar section
- Each feature is independently activated  -  start with one and add more over time
- You set your own pricing for parent-facing services and keep 100% of the revenue
- Pick Up Safety Alerts, Parent SMS Communications, and Family SMS Access Portal all live here
- Subscription management, billing, and invitations all handled automatically
- Visible only to owner and authorized staff  -  parents never see this section`,
    howto: [
      "Look for the '$$$ Revenue Hub' section in the sidebar  -  click to expand it",
      "Three revenue features are inside: Pick Up Safety Alerts, Parent SMS Communications, and Family Communications Hub",
      "Each feature has its own Subscribe button  -  activate them one at a time as your center grows",
      "Once subscribed, set your parent-facing pricing and activate parents from the Family SMS Access Portal",
      "Revenue features are gated by subscription tier  -  Premium and above unlock all three",
      "Go to Owner Tools -> Pricing Manager to update prices for any revenue feature at any time"
    ]
  },
  {
    icon: Bell,
    color: "text-orange-600",
    bg: "bg-orange-50",
    title: "Pickup Safety Alerts",
    sidebar: "Revenue Hub -> Pick Up Safety Alerts",
    marketing: `Pickup Safety Alerts

Send real-time SMS alerts to parents the moment their child is signed in or out  -  including the exact time and the name of the person who picked them up. Every pickup is documented with a permanent timestamp giving parents immediate confirmation and giving you a complete audit trail. Fully compliant with your state's required pickup documentation standards.

Key Benefits:
- Instant text alert to parents at every drop-off and pickup
- Alert includes exact time, child name, and pickup person name
- Complete timestamp audit trail for every sign-in event
- Configurable per child  -  set exactly which events trigger alerts
- Authorized pickup list management built into each child's profile
- Supports the center's own parent billing for the alert service`,
    howto: [
      "Go to Revenue Hub -> Pick Up Safety Alerts in the sidebar",
      "Subscribe to activate (monthly or annual plan)",
      "Configure your alert settings  -  choose which events send a text (drop-off, pickup, or both)",
      "Alerts activate for all enrolled children automatically",
      "Parents receive a text at every sign-in/out event with the time and pickup person's name",
      "All alert history is stored and viewable in your dashboard"
    ]
  },
  {
    icon: BarChart3,
    color: "text-violet-600",
    bg: "bg-violet-50",
    title: "Compliance Dashboard",
    sidebar: "Compliance in the sidebar",
    marketing: `Compliance Dashboard

See your entire center's compliance health at a glance with a real-time score and color-coded status for every requirement. Staff clearances, facility inspections, meal compliance, and documentation requirements all tracked in one dashboard  -  built for state licensing requirements and always ready for an unannounced visit.

Key Benefits:
- Real-time compliance score shows your overall readiness as a percentage
- Color-coded status: Green (current), Yellow (expiring soon), Red (expired or missing)
- Covers staff clearances, facility requirements, meal compliance, and inspection items
- Inspection Mode simulates a state walk-through so you can prepare
- Automatic email alerts before anything expires  -  no surprises
- Health & Safety Basics tracked as one-time completion  -  never falsely flags as expired`,
    howto: [
      "Go to Compliance in the sidebar",
      "The dashboard shows your overall compliance score at the top with a percentage",
      "Red items are expired or missing  -  click any to go directly to the record that needs updating",
      "Yellow items are expiring within 30 days  -  address these proactively",
      "Click Inspection Mode to run through a simulated state inspection checklist",
      "The Staff Compliance section shows every clearance for every staff member with one-click access"
    ]
  },
  {
    icon: Activity,
    color: "text-rose-600",
    bg: "bg-rose-50",
    title: "Compliance Readiness Score",
    sidebar: "Compliance Dashboard + Home Page",
    marketing: `Compliance Readiness Score  -  Know Your Score Before the Inspector Does

The Compliance Readiness Score is the heartbeat of ToDays Kidz Simple Sign-In. It monitors every staff clearance, facility requirement, certification, and document across your entire center and calculates a live compliance percentage at all times. Green means ready. Yellow means act soon. Red means act now.

This is the feature that protects your license.

Key Benefits:
- Live percentage score updated automatically as anything expires or is renewed
- Monitors staff clearances, facility items, meal compliance, and documentation simultaneously
- Red, yellow, and green color system makes status instantly obvious at a glance
- Drill down from the score directly to the specific item that needs attention
- Score history lets you track improvement over time
- The most important number in your center  -  visible on the home page every day`,
    howto: [
      "Your Readiness Score is always visible  -  on the Home page (via the Readiness toggle) and at the top of the Compliance Dashboard",
      "A score of 100% means every tracked item is current and valid",
      "Click any red or yellow item in the dashboard to jump directly to that staff member or facility record",
      "As you update clearances and upload documents, the score recalculates in real time",
      "Use the score as your daily check  -  if it drops below 100%, something needs attention",
      "Go to Compliance -> Compliance Dashboard to see the full breakdown behind the score"
    ]
  },
  {
    icon: ToggleRight,
    color: "text-emerald-600",
    bg: "bg-emerald-50",
    title: "Home Page Readiness Toggle",
    sidebar: "Home Page  -  top of dashboard",
    marketing: `Home Page Readiness Toggle

Your compliance score lives on your home page dashboard, always visible the moment you log in. The Readiness Toggle lets you instantly show or hide the full compliance readiness panel  -  giving you a one-glance daily check without navigating anywhere. See your score, see what's expiring, and know exactly where your center stands before the day begins.

Key Benefits:
- Compliance score visible on the home page  -  no navigation required
- Toggle the readiness panel open or closed without leaving your dashboard
- Expiring and expired items surfaced directly on the home screen
- The fastest way to start every day informed and in control
- Staff and co-owners can view their assigned compliance status from the same screen`,
    howto: [
      "Log in to the app  -  the Readiness toggle is at the top of the Home page dashboard",
      "Click the Readiness toggle to expand the compliance readiness panel",
      "The panel shows your current score and any items that are red or yellow",
      "Click any flagged item to navigate directly to the record that needs updating",
      "Toggle it closed to return to your normal home page view",
      "The score updates automatically  -  checking it daily takes under 10 seconds"
    ]
  },
  {
    icon: Archive,
    color: "text-indigo-600",
    bg: "bg-indigo-50",
    title: "Inspection Binder",
    sidebar: "Compliance -> Inspection Binder",
    marketing: `Inspection Binder

Your complete digital inspection binder  -  organized exactly the way state inspectors review it. Every required document, staff clearance, policy acknowledgment, and facility record in one structured digital binder. Pull it up on any device during an unannounced visit and walk an inspector through your entire compliance file in minutes, not hours.

Key Benefits:
- All compliance documents organized in state-inspection order
- Accessible on any device  -  iPad, phone, or desktop  -  instantly
- Never scramble for paperwork during an unannounced visit again
- Includes staff clearances, facility records, meal documentation, and policies
- Updated automatically as documents are added or renewed
- Print a full binder report for physical backup at any time`,
    howto: [
      "Go to Compliance in the sidebar, then select Inspection Binder",
      "The binder is pre-organized in the order state inspectors typically review items",
      "Each section shows green (complete), yellow (expiring), or red (missing/expired) status",
      "Click any section to view the documents or records within it",
      "During an inspection, open the binder on your iPad and walk the inspector through each section",
      "Click Print Binder Report to generate a full printable version for your physical files"
    ]
  },
  {
    icon: CreditCard,
    color: "text-green-600",
    bg: "bg-green-50",
    title: "Billing & Invoicing",
    sidebar: "Billing & Payments in the sidebar",
    marketing: `Billing & Invoicing

Integrated billing powered by Square. Send professional invoices, process payments, manage parent portal subscriptions, and track your entire revenue cycle from one screen. Supports one-time payments, automatic recurring subscriptions, and the Zelle payment tracking system for centers that prefer bank transfers. Everything is documented and exportable for your records.

Key Benefits:
- Square-powered checkout  -  professional payment links sent directly to parents
- Automatic recurring billing for parent portal subscriptions
- Zelle payment tracking with overdue report and automated reminders
- Complete payment history for every parent
- Invoice management with outstanding balance tracking
- Revenue reports for your center's financial planning`,
    howto: [
      "Go to Billing & Payments in the sidebar",
      "View all invoices, payment history, and outstanding balances at a glance",
      "Click New Invoice, select a parent, and enter the amount and description",
      "Click Send  -  the parent receives a Square payment link by email or text",
      "Payments are tracked automatically and marked received when processed",
      "For Zelle payments, use the Zelle Overdue Report to track who has paid and send reminders to those who haven't",
      "Parent portal monthly fees are collected automatically  -  no manual invoicing needed"
    ]
  },
  {
    icon: Upload,
    color: "text-cyan-600",
    bg: "bg-cyan-50",
    title: "Staff Health Form Upload",
    sidebar: "Staff -> Records & Compliance -> Staff Health Form section",
    marketing: `Staff Health Form & Document Storage

Attach physical exam documents, health records, and any required state forms directly to each staff member's profile. Documents are stored securely in the cloud and available instantly during inspections  -  no filing cabinets, no lost paperwork. Print or download any document from any device at any time with one click.

Key Benefits:
- Upload PDFs, photos, and images directly to any staff record
- Multiple documents supported per category  -  stacked with a count for easy access
- Download or print any document from any device instantly
- Documents are permanently stored  -  they never expire from the system
- Staff Health Form section is separate from clearances for easy inspection navigation`,
    howto: [
      "Go to Staff -> Records & Compliance in the sidebar",
      "Open a staff member's expanded record",
      "Scroll to the Staff Health Form section (blue card, above the clearances table)",
      "Click the blue Upload button to attach a PDF, photo, or image",
      "Once uploaded, the file appears with a Download button and a Print button",
      "To print, click Print  -  the document opens in a new tab and the print dialog appears automatically",
      "Multiple uploads stack with a count badge  -  click the badge to expand and see all documents"
    ]
  },
  {
    icon: Utensils,
    color: "text-orange-600",
    bg: "bg-orange-50",
    title: "Center Menus",
    sidebar: "Center Menus in the sidebar",
    marketing: `Center Menus

Create, manage, and print professional weekly menus for every meal service at your center  -  Breakfast, Lunch, and Afternoon Snack. Post printed menus in classrooms and common areas, share them digitally with families through the parent portal, and maintain a complete menu history for CACFP documentation and state inspection.

Key Benefits:
- Create weekly menus for Breakfast, Lunch, and Afternoon Snack
- Professional print-ready layout with your center name and logo
- Share menus with parents through the Family Communications Hub
- Complete menu history stored for CACFP audits and state inspections
- Supports all age group nutritional requirements`,
    howto: [
      "Go to Center Menus in the sidebar",
      "Select the meal type  -  Breakfast/Snack or Lunch",
      "Select the week using the date picker",
      "Fill in your food items for each day of the week",
      "Click Save to store the menu",
      "Click Print to generate a formatted menu ready to post or share",
      "Share menus with parents by sending them through Family Communications Hub as an announcement"
    ]
  },
  {
    icon: ClipboardCheck,
    color: "text-green-700",
    bg: "bg-green-50",
    title: "Meal Compliance Tracker",
    sidebar: "Compliance -> Meal Compliance",
    marketing: `Meal Compliance Tracker

Track daily meal counts, attendance at each meal service, and ensure your center meets all CACFP (Child and Adult Care Food Program) requirements. Document every meal served, maintain accurate counts by age group, and generate the reports you need for your CACFP sponsor and state licensing inspections  -  all in one place.

Key Benefits:
- Daily meal count tracking for Breakfast, Lunch, and Snack
- Attendance tracking by meal service and age group
- CACFP compliance documentation built into the daily workflow
- Complete meal history available for audits and sponsor reviews
- Color-coded compliance indicators show issues before they become violations`,
    howto: [
      "Go to Compliance in the sidebar, then select Meal Compliance",
      "Select today's date to view or enter meal counts",
      "Enter attendance and meal counts for each meal service (Breakfast, Lunch, Snack)",
      "The system tracks counts by age group automatically based on enrolled children",
      "Green indicators confirm compliance  -  yellow or red flags need attention before your next CACFP report",
      "View historical meal records by date range for sponsor audits or state inspections"
    ]
  },
  {
    icon: ShoppingCart,
    color: "text-orange-700",
    bg: "bg-orange-50",
    title: "Menu, Inventory & Shopping List",
    sidebar: "Menu, Inventory & Shopping List in the sidebar",
    marketing: `Menu, Inventory & Shopping List

Scan your weekly menu and your grocery receipts or invoices, and let the system match what you bought against what your menu calls for  -  flagging any ingredient you're missing and building your shopping list automatically. No more manually cross-checking receipts against recipes. Every scan is read and matched automatically, so you know exactly what to buy before the next CACFP-compliant week starts.

Key Benefits:
- Scan a menu photo or PDF and the system reads every meal and ingredient automatically
- Scan receipts or invoices and have items matched against your menu instantly
- Automatic shopping list built from anything your menu needs that your receipts don't show
- Signature-tier document storage included for unlimited menu and receipt signatures
- Clear monthly scan allowance shown up front so you always know what's included`,
    howto: [
      "Go to Menu, Inventory & Shopping List in the sidebar",
      "Click Scan Menu and upload a photo or PDF of your weekly menu",
      "Click Scan Receipt/Invoice for each grocery receipt or vendor invoice you want matched",
      "Review the Match to Menu panel  -  items you already have are checked off automatically",
      "Anything your menu needs that wasn't found on a receipt appears on your Shopping List",
      "Your remaining monthly scans are shown at the top of the page at all times",
      "If an item lands in Needs Review and you don't need it tracked, click the trash-can icon to remove it  -  it just won't count toward your shopping list anymore",
      "When you type ingredients into Split into ingredients, the system checks if that exact same list was already used for another dish this month. If it's an exact match, nothing saves yet  -  you'll see a choice: \"Keep as separate dish\" (if it really is different) or \"Dismiss\" (if it's a duplicate)",
      "If you try to add a brand-new item but one with that name already exists, the system automatically switches you to \"Match to existing item\" and fills in the search for you  -  no dead-end error",
      "To fix a typo or combine two ingredients that are really the same thing, go to Master Item List, check the two items, and pick which one should stay as the \"main\" item. The other one moves to Archived, labeled \"Merged into [main item].\" This can't be undone",
      "To clean up items you no longer use, open Master Item List and click Archived (next to \"+ Add Item\"). From there you can permanently delete an item  -  this also deletes its history, so there's a warning before you confirm",
      "If you click the circular arrow to move a confirmed item back to Needs Review, it now starts completely fresh  -  no leftover suggested match. You'll see the normal Match / Add / Split / Remove choices again"
    ]
  },
  {
    icon: FolderOpen,
    color: "text-cyan-700",
    bg: "bg-cyan-50",
    title: "Staff Document Uploads",
    sidebar: "Staff -> Records & Compliance -> Documents column",
    marketing: `Staff Document Uploads

Attach digital copies of any staff document directly to their profile  -  clearance certificates, training completions, credentials, ID copies, and more. Every document is stored securely in the cloud, available instantly on any device, and never lost. Multiple documents stack neatly with a count badge so records stay organized no matter how many files are attached.

Key Benefits:
- Upload PDFs, photos, and images to any clearance row
- Multiple documents per category stack with a count badge for easy access
- Download or print any document instantly from any device
- Documents are stored permanently  -  never expire from the system
- Organized alongside each clearance for fast inspection navigation`,
    howto: [
      "Go to Staff -> Records & Compliance and expand a staff member's record",
      "Find the clearance row you want to attach a document to",
      "Click the Upload button on that row to attach a PDF, photo, or image",
      "When multiple files are uploaded, they stack into a count badge (e.g. '3 files')",
      "Click the count badge to expand and see all documents for that clearance",
      "Click Download to retrieve a file or Print to open it in your browser for printing"
    ]
  },
  {
    icon: Inbox,
    color: "text-teal-600",
    bg: "bg-teal-50",
    title: "Enrollment Package",
    sidebar: "Children -> Compliance Records -> Enrollment Packet tab",
    marketing: `Enrollment Package

Send new families a complete enrollment packet with one click  -  no printing, no drop-off, no chasing forms. Parents receive a secure private link by email and/or SMS. They download each blank form, complete it, and upload it back  -  all from their phone or computer. No app download or login required.

Key Benefits:
- Sends all 8 required compliance forms to a new family in a single click
- Parents return completed forms digitally  -  uploads auto-file into Records Management
- Add center-specific forms (Parent Handbook, Field Trip Policy, etc.) as Return Required or Acknowledge Only  -  these do not count toward your state compliance score
- Track every family's return status in real time  -  Not Sent / Waiting / 3 of 8 / Complete
- Click any status badge to see a form-by-form breakdown of what is received and what is still outstanding
- Permission-controlled  -  manage which staff roles can send packets in Owner Tools`,
    howto: [
      "Go to Children -> Compliance Records -> Enrollment Packet tab",
      "Upload blank copies of your 8 required forms in the Blank Form Templates section",
      "Optionally add center-specific forms in Additional Center Forms  -  mark each as Return Required or Acknowledge Only  -  these do not count toward your state compliance score",
      "In Send Enrollment Packets, find the child and click Send Packet  -  the parent receives a secure private link by email and/or SMS",
      "Parent clicks the link, downloads each blank form, completes it, and uploads it back  -  no login or app download required",
      "Track return progress from the status badges on the child list  -  click any badge to see a form-by-form breakdown of what is received and what is still outstanding"
    ]
  },
  {
    icon: RefreshCw,
    color: "text-teal-600",
    bg: "bg-teal-50",
    title: "Automated Record Renewal & Compliance Tracking",
    sidebar: "Children -> Compliance Records -> Records Management tab",
    marketing: `Automated Record Renewal & Compliance Tracking

Stop manually tracking when Emergency Contact and Financial Contract forms expire. Set your renewal period once  -  the system monitors every child's form dates in the background and automatically sends email and text reminders to parents when their form is due. When a parent resubmits, the record moves to your Resubmitted list ready to print and file. Run a compliance print report for state inspectors in one click.

Key Benefits:
- Automatic email and text reminders sent to parents when forms are due  -  no manual tracking
- Separate trackers for Emergency Contact and Financial Contract forms, each with its own interval
- Enable or disable either tracker independently without losing your saved settings
- Resubmitted records include checkbox selection and Print All for batch filing
- One-click compliance print report  -  formatted for state inspectors with form-by-form status per child`,
    howto: [
      "Go to Children -> Compliance Records -> Records Management tab",
      "Open the Emergency Contact Renewal Tracker  -  click the green header to expand it",
      "Toggle the switch to Enabled and set your review period (e.g. 6 months)  -  press Enter or click away to save",
      "Open the Financial Contract Renewal Tracker  -  click the blue header to expand it and configure the same way",
      "Once enabled, the system works automatically  -  when a child's form passes the renewal period you set, the parent receives an email and/or text with a link to resubmit. No manual action needed",
      "When a parent resubmits, their record moves to the Resubmitted section  -  check the boxes next to the records you want and click Print All Selected to generate a filing sheet for your records",
      "To print a full compliance summary for a state inspector, go to the Enrollment Packet tab  -  click Print on any child's row or Print All in the header to open a formatted report"
    ]
  },
];

export default function FeaturesGuidePage() {
  const [tab, setTab] = useState("howto");
  const [open, setOpen] = useState(null);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { currentTier } = useFeatureAccess("tour_planner");
  const canCopyPublicLink = user?.role === "owner" && currentTier === "signature";

  const handleCopyPublicLink = () => {
    const url = `${window.location.origin}/about`;
    navigator.clipboard.writeText(url);
    toast.success("Public link copied — safe to share in ads or on your website");
  };

  return (
    <AppShell pageTitle="Features Guide" breadcrumb>
      {/* Header */}
      <div className="rounded-2xl bg-gradient-to-r from-teal-600 to-emerald-600 p-6 mb-6 text-white" data-testid="features-guide-hero">
        <h1 className="text-2xl font-bold mb-1">ToDays Kidz Simple Sign-In  -  Features Guide</h1>
        <p className="text-teal-100 text-sm max-w-2xl">Complete reference for every feature. Use the How-To Guide to learn the app, or copy the About This App text for your website.</p>
      </div>

      {/* Tab switcher */}
      <div className="flex items-center justify-between flex-wrap gap-3 mb-6">
        <div className="flex gap-1 bg-slate-100 rounded-xl p-1 w-fit">
          <button onClick={() => setTab("howto")} className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition border-2 ${tab === "howto" ? "bg-white shadow text-slate-800 border-emerald-400" : "text-slate-500 hover:text-slate-700 border-transparent"}`} data-testid="howto-tab">
            How-To Guide
          </button>
          <button onClick={() => setTab("marketing")} className={`px-5 py-2.5 rounded-lg text-sm font-semibold transition border-2 ${tab === "marketing" ? "bg-white shadow text-teal-700 border-emerald-400" : "text-slate-500 hover:text-slate-700 border-transparent"}`} data-testid="marketing-tab">
            About This App
          </button>
        </div>
        {tab === "marketing" && canCopyPublicLink && (
          <button
            onClick={handleCopyPublicLink}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg text-sm font-semibold bg-teal-600 text-white hover:bg-teal-700 transition"
            data-testid="copy-public-about-link-btn"
          >
            <LinkIcon className="w-4 h-4" />
            Copy Public Link
          </button>
        )}
      </div>

      {/* How-To Guide */}
      {tab === "howto" && (
        <div className="space-y-3">
          <p className="text-xs text-slate-400 mb-4">Click any feature to expand step-by-step instructions.</p>
          {FEATURES.map((f, idx) => (
            <div key={idx} className="rounded-2xl border border-slate-200 bg-white overflow-hidden" data-testid={`feature-card-${idx}`}>
              <button className="w-full flex items-center gap-3 px-5 py-4 text-left hover:bg-slate-50 transition"
                onClick={() => setOpen(open === idx ? null : idx)}>
                <div className={`w-10 h-10 rounded-xl ${f.bg} flex items-center justify-center flex-shrink-0`}>
                  <f.icon className={`h-5 w-5 ${f.color}`} />
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-slate-900">{f.title}</p>
                  <p className="text-xs text-slate-400 mt-0.5">Found at: {f.sidebar}</p>
                </div>
                <span className={`text-slate-400 transition-transform ${open === idx ? "rotate-90" : ""}`}>▶</span>
              </button>
              {open === idx && (
                <div className="border-t border-slate-100 px-5 py-4 bg-slate-50">
                  <p className="text-xs font-bold text-slate-500 uppercase tracking-wide mb-3">Step-by-Step</p>
                  <ol className="space-y-2">
                    {f.howto.map((step, s) => (
                      <li key={s} className="flex gap-3 text-sm text-slate-700">
                        <span className="w-6 h-6 rounded-full bg-teal-100 text-teal-700 text-xs font-bold flex items-center justify-center flex-shrink-0 mt-0.5">{s + 1}</span>
                        {step}
                      </li>
                    ))}
                  </ol>
                </div>
              )}
            </div>
          ))}
        </div>
      )}

      {/* Marketing / Website Copy */}
      {tab === "marketing" && (
        <div className="space-y-4">
          {FEATURES.map((f, idx) => (
            <div key={idx} className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
              <div className="flex items-center gap-3 px-5 py-3 border-b border-slate-100">
                <div className={`w-8 h-8 rounded-lg ${f.bg} flex items-center justify-center flex-shrink-0`}>
                  <f.icon className={`h-4 w-4 ${f.color}`} />
                </div>
                <p className="font-semibold text-slate-800 flex-1">{f.title}</p>
              </div>
              <div className="px-5 py-4">
                <textarea
                  readOnly
                  value={f.marketing}
                  className="w-full text-sm text-slate-700 bg-slate-50 border border-slate-200 rounded-xl p-3 resize-none focus:outline-none leading-relaxed"
                  rows={f.marketing.split("\n").length + 1}
                  onClick={e => e.target.select()}
                />
              </div>
            </div>
          ))}
        </div>
      )}
    </AppShell>
  );
}
